/**
 * Electing out of the centralized partnership audit regime (BBA), Form 1065
 * Schedule B question 33 and Schedule B-2 (Linear PG-19). The API's mmllc
 * return model and the wizard's financial-data step both ask this module, so
 * the question is offered exactly when the return can carry the election.
 *
 * Instructions for Form 1065 (2025), p. 30, "Question 33" (irs.gov, read
 * 2026-10-17): "Answer 'Yes' if an eligible partnership chooses to elect out
 * of the centralized partnership audit regime for the tax year and enter the
 * total from Schedule B-2, Part III, line 3. If making the election, attach a
 * completed Schedule B-2 to Form 1065. An election out of the centralized
 * partnership audit regime can only be made on a timely filed return
 * (including extensions). A partnership is an eligible partnership for the tax
 * year if it has 100 or fewer eligible partners in that year. Eligible
 * partners are individuals, C corporations, S corporations, foreign entities
 * that would be C corporations if they were domestic entities, and estates of
 * deceased partners." A partnership, trust, disregarded entity, an estate
 * other than a deceased partner's, or a nominee breaks eligibility (same page).
 *
 * Instructions for Schedule B-2 (December 2018), p. 1, "Part I": "provide the
 * correct U.S. Tax Identification Number (TIN) of the partner. Inaccurate TINs
 * will result in validation errors, and the IRS may determine that the
 * election is not valid." Reg. 301.6221(b)-1(c)(2) requires it and T.D. 9829
 * declined an alternative identifier, so a member with no SSN or ITIN (most of
 * our customers' members) keeps the partnership on the partnership
 * representative path (research/mmllc-k2k3.md).
 */

/** Who a partner is, for eligibility (i1065 2025 p. 30). */
export type BbaPartnerKind =
  | "individual"
  | "c_corporation"
  | "s_corporation"
  | "foreign_c_corporation"
  | "estate_of_deceased_partner"
  | "partnership"
  | "trust"
  | "disregarded_entity"
  | "other";

/** Schedule B-2 Part I column 3 codes (i1065sb2 p. 1). */
export type BbaPartnerCode = "I" | "C" | "S" | "F" | "E";

export interface BbaPartner {
  kind: BbaPartnerKind;
  /** SSN or ITIN for an individual, EIN for an entity; digits or dashed. */
  usTin?: string | null;
}

export type BbaElectionRefusal =
  | "no_partners"
  | "late_return"
  | "too_many_partners"
  | "ineligible_partner"
  | "missing_tin";

export type BbaElectionEligibility =
  | { eligible: true }
  | { eligible: false; reason: BbaElectionRefusal; partners?: number[] };

/**
 * The Part I rows of Schedule B-2 we fill (page 1 only; Part IV on page 2 is
 * not prepared automatically). The law's limit is 100 partners.
 */
export const BBA_ELECTION_MAX_PARTNERS = 15;

/** The wizard's answer, `financialData.form1065.details.bbaElectOut` ('yes' / 'no'). */
export const BBA_ELECT_OUT_DETAIL_KEY = "bbaElectOut";

const CODES: Partial<Record<BbaPartnerKind, BbaPartnerCode>> = {
  individual: "I",
  c_corporation: "C",
  s_corporation: "S",
  foreign_c_corporation: "F",
  estate_of_deceased_partner: "E",
};

/** The Schedule B-2 code, or null for a partner that breaks eligibility. */
export function bbaPartnerCode(kind: BbaPartnerKind): BbaPartnerCode | null {
  return CODES[kind] ?? null;
}

function digitsOf(value: unknown): string {
  return typeof value === "string" ? value.replace(/\D/g, "") : "";
}

/**
 * An SSN or ITIN by its shape: an ITIN is 9xx-GG-xxxx with the group in 50-65,
 * 70-88, 90-92 or 94-99; an SSN never has area 000, 666 or 9xx, group 00 or
 * serial 0000.
 */
export function isIndividualUsTin(value: unknown): boolean {
  const d = digitsOf(value);
  if (!/^\d{9}$/.test(d) || typeof value !== "string") return false;
  const area = Number(d.slice(0, 3));
  const group = Number(d.slice(3, 5));
  const serial = Number(d.slice(5));
  if (d.startsWith("9")) {
    return (
      (group >= 50 && group <= 65) ||
      (group >= 70 && group <= 88) ||
      (group >= 90 && group <= 92) ||
      (group >= 94 && group <= 99)
    );
  }
  return area !== 0 && area !== 666 && group !== 0 && serial !== 0;
}

/**
 * A member's U.S. taxpayer number type. The wizard's own field holds an SSN or
 * ITIN; a number the company record typed as an EIN (onboarding) is an
 * entity's number, never an individual's.
 */
export type PartnerTinType = "SSN" | "ITIN" | "EIN";

const INDIVIDUAL_TIN_TYPES: ReadonlySet<string> = new Set(["SSN", "ITIN"]);

/**
 * The 9 digits an individual member's SSN or ITIN prints as (Schedule B-2
 * Part I, K-1 item E), or null. Most EINs have an SSN's shape (81-1234567 reads
 * as area 811, group 23, serial 4567), so the shape alone never decides: a
 * number typed EIN, or an untyped one the company record holds as an EIN
 * (`einsOnFile`, the digits of every shareholder tax id typed EIN), is not
 * one. An untyped number of an individual's shape is accepted: the wizard's
 * field asked for an SSN or ITIN before types were stored.
 */
export function individualUsTinOf(
  partner: { tin?: unknown; tinType?: unknown },
  options: { einsOnFile?: ReadonlySet<string> } = {},
): string | null {
  const type =
    typeof partner.tinType === "string"
      ? partner.tinType.trim().toUpperCase()
      : "";
  if (type && !INDIVIDUAL_TIN_TYPES.has(type)) return null;
  if (!isIndividualUsTin(partner.tin)) return null;
  const digits = digitsOf(partner.tin);
  if (!type && options.einsOnFile?.has(digits)) return null;
  return digits;
}

/** "***-**-1234": a U.S. TIN with only its last four digits shown. */
export function maskUsTin(value: string): string {
  return `***-**-${digitsOf(value).slice(-4)}`;
}

function hasUsTin(partner: BbaPartner): boolean {
  if (partner.kind === "individual") return isIndividualUsTin(partner.usTin);
  const d = digitsOf(partner.usTin);
  return /^\d{9}$/.test(d) && !d.startsWith("00");
}

/**
 * Whether this partnership can elect out on this return. Checks, in order: a
 * timely return, the number of partners we can list, every partner an
 * eligible type, every partner a U.S. TIN.
 */
export function bbaElectionOutEligibility(
  partners: readonly BbaPartner[],
  options: { late: boolean },
): BbaElectionEligibility {
  if (partners.length === 0) return { eligible: false, reason: "no_partners" };
  if (options.late) return { eligible: false, reason: "late_return" };
  if (partners.length > BBA_ELECTION_MAX_PARTNERS) {
    return { eligible: false, reason: "too_many_partners" };
  }
  const ineligible = partners.flatMap((p, i) =>
    bbaPartnerCode(p.kind) ? [] : [i],
  );
  if (ineligible.length > 0) {
    return {
      eligible: false,
      reason: "ineligible_partner",
      partners: ineligible,
    };
  }
  const missing = partners.flatMap((p, i) => (hasUsTin(p) ? [] : [i]));
  if (missing.length > 0) {
    return { eligible: false, reason: "missing_tin", partners: missing };
  }
  return { eligible: true };
}

/**
 * The customer's choice. Owner decision (PG-19): an eligible partnership
 * elects out by default; only an explicit "no" keeps the partnership
 * representative.
 */
export function wantsBbaElectionOut(answer: unknown): boolean {
  return !(typeof answer === "string" && answer.trim().toLowerCase() === "no");
}
